import { z } from 'zod';
import apiClient from '../client';
import type { AdminTradeMateAccountsResponse } from '@/types/tradeMate';

// Schema for type validations
const userSchema = z.object({
  userId: z.number(),
  userName: z.string(),
  totalFee: z.number(),
  totalFund: z.number(),
  status: z.boolean(),
});

const userActivitySchema = z.object({
  id: z.number(),
  userId: z.number(),
  activityType: z.string(),
  description: z.string(),
  metadata: z.any().nullable(),
  ipAddress: z.string(),
  userAgent: z.string(),
  success: z.boolean(),
  errorMessage: z.string().nullable(),
  createdAt: z.string(),
  user: z.object({
    id: z.number(),
    email: z.string(),
    fullName: z.string(),
    userName: z.string(),
  }),
});

const userAccountSchema = z.object({
  id: z.number(),
  userId: z.number(),
  accountId: z.string(),
  accountName: z.string(),
  brokerName: z.string(),
  platform: z.string(),
  server: z.string(),
  login: z.string(),
  password: z.string(),
  balance: z.number(),
  challengeType: z.string(),
  status: z.string(),
  poolStatus: z.string(),
  challengePhase: z.string().nullable(),
  phaseStartDate: z.string().nullable(),
  previousAccountId: z.string().nullable(),
  // The MetaApi account this trading account belongs to. Replaces the old
  // sourceTable shard name: which token owns a row is now a real relation.
  token: z.object({ id: z.number(), name: z.string() }).nullable().optional(),
  tokenId: z.number().nullable().optional(),
  sourceTable: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  user: z.object({
    id: z.number(),
    email: z.string(),
    emailVerified: z.boolean(),
    phone: z.string().nullable(),
    fullName: z.string(),
    userName: z.string(),
    profile: z.object({
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      publicUserName: z.string().nullable(),
      name: z.string().nullable(),
      country: z.string().nullable(),
      town: z.string().nullable(),
      state: z.string().nullable(),
    }),
  }),
});

const paymentAddOnsSchema = z
  .object({
    accountProtection: z.boolean().optional(),
    accountInsurance: z.boolean().optional(),
    profitSplit100: z.boolean().optional(),
    fastPayout: z.boolean().optional(),
    fastPayout7Day: z.boolean().optional(),
    secondAccount: z.boolean().optional(),
    secondAccountHalfOff: z.boolean().optional(),
    bogo: z.boolean().optional(),
    scalingPlan: z.boolean().optional(),
    payoutProtection: z.boolean().optional(),
    couponId: z.number().optional(),
    couponCode: z.string().optional(),
  })
  .passthrough()
  .nullable()
  .optional();

const paymentSchema = z.object({
  id: z.number(),
  userId: z.number(),
  platform: z.string(),
  challenge: z.string(),
  balance: z.number(),
  amount: z.number(),
  paidAmount: z.number().nullable().optional(),
  finalAmount: z.number().nullable().optional(),
  trackId: z.string(),
  orderId: z.string(),
  accountId: z.string().nullable(),
  currency: z.string().nullable(),
  method: z.string(),
  status: z.string(),
  createdAt: z.string(),
  notes: z.string(),
  updatedAt: z.string(),
  addOns: paymentAddOnsSchema,
  accountProtectionUsed: z.boolean().optional(),
  user: z.object({
    id: z.number(),
    email: z.string(),
    emailVerified: z.boolean(),
    phone: z.string().nullable(),
    fullName: z.string(),
    userName: z.string(),
    profile: z.object({
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      publicUserName: z.string().nullable(),
      name: z.string().nullable(),
      country: z.string().nullable(),
      town: z.string().nullable(),
      state: z.string().nullable(),
    }),
  }),
});

const dashboardSummarySchema = z.object({
  userList: z.array(userSchema),
  totalUsers: z.number().optional(),
  activeUsers: z.number().optional(),
  revenue: z.number().optional(),
  // Add other summary fields as needed
});

const paginationSchema = z.object({
  currentPage: z.number(),
  totalPages: z.number(),
  totalItems: z.number(),
  itemsPerPage: z.number(),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean(),
});



const paymentsResponseSchema = z.object({
  success: z.boolean(),
  data: z.union([
    z.array(paymentSchema), // For backward compatibility
    z.object({
      data: z.array(paymentSchema),
      pagination: paginationSchema,
    }),
  ]),
  message: z.string(),
});

const userAccountsResponseSchema = z.object({
  success: z.boolean(),
  data: z.union([
    z.array(userAccountSchema), // For backward compatibility
    z.object({
      data: z.array(userAccountSchema),
      pagination: paginationSchema,
    }),
  ]),
  message: z.string(),
});

const userActivitiesResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    activities: z.array(userActivitySchema),
    pagination: paginationSchema,
  }),
  message: z.string(),
});

const accountsResponseSchema = z.object({
  success: z.boolean(),
  data: z.union([
    z.array(userAccountSchema), // For backward compatibility
    z.object({
      data: z.array(userAccountSchema),
      pagination: paginationSchema,
    }),
  ]),
  message: z.string(),
});

const poolAccountSchema = z.object({
  id: z.number(),
  platform: z.string(),
  balance: z.number(),
  targetPoolSize: z.number(),
  currentPoolSize: z.number(),
  minimumThreshold: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const poolAccountsResponseSchema = z.object({
  success: z.boolean(),
  data: z.union([
    z.array(poolAccountSchema), // For backward compatibility
    z.object({
      data: z.array(poolAccountSchema),
      pagination: paginationSchema,
    }),
  ]),
  message: z.string(),
});

// Payout request schemas
const payoutRequestSchema = z.object({
  accountId: z.string(),
  balance: z.number(),
  equity: z.number(),
  phone: z.string(),
  accountSize: z.number(),
  email: z.string(),
  name: z.string(),
});

const payoutRequestResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    id: z.number(),
    accountId: z.string(),
    balance: z.number(),
    equity: z.number(),
    phone: z.string(),
    accountSize: z.number(),
    email: z.string(),
    name: z.string(),
    status: z.string(),
    createdAt: z.string(),
  }),
  message: z.string(),
});

const adminPayoutRequestSchema = z.object({
  id: z.number(),
  accountId: z.string(),
  userId: z.number(),
  balance: z.number(),
  equity: z.number(),
  email: z.string(),
  phone: z.string(),
  accountSize: z.number(),
  status: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  user: z.object({
    id: z.number(),
    email: z.string(),
    emailVerified: z.boolean(),
    phone: z.string().nullable(),
    fullName: z.string(),
    userName: z.string(),
    profile: z.object({
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      publicUserName: z.string().nullable(),
      name: z.string().nullable(),
      country: z.string().nullable(),
      town: z.string().nullable(),
      state: z.string().nullable(),
    }),
  }),
  mtAccount: z.object({
    accountId: z.string(),
    accountName: z.string(),
    platform: z.string(),
    challengeType: z.string(),
    status: z.string(),
  }),
});

const adminPayoutRequestsResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    data: z.array(adminPayoutRequestSchema),
    pagination: paginationSchema,
  }),
  message: z.string(),
});

const updatePayoutRequestSchema = z.object({
  status: z.enum(["approved", "rejected", "pending"]),
  reason: z.string().optional(),
});

const updatePayoutRequestResponseSchema = z.object({
  success: z.boolean(),
  data: adminPayoutRequestSchema,
  message: z.string(),
});

// Subscriber schemas
const subscriberSchema = z.object({
  id: z.number(),
  email: z.string(),
  isActive: z.boolean(),
  subscribedAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});



const subscribersResponseSchema = z.object({
  success: z.boolean(),
  data: z.union([
    z.array(subscriberSchema),
    z.object({
      data: z.array(subscriberSchema),
      pagination: paginationSchema,
    }),
  ]),
  message: z.string(),
});

// Inactive Account schemas
const inactiveAccountSchema = z.object({
  id: z.number(),
  accountId: z.string(),
  login: z.string(),
  userId: z.number().nullable(),
  userEmail: z.string(),
  userFullName: z.string(),
  challengeType: z.string(),
  challengePhase: z.number().nullable(),
  balance: z.number(),
  daysSinceActivity: z.number(),
  lastActivityDate: z.string(),
  detectedAt: z.string(),
  hasAutoTermination: z.boolean(),
  autoTermination: z.object({
    enabledAt: z.string(),
    enabledBy: z.string(),
    emailSentAt: z.string().nullable(),
  }).nullable(),
});

const inactiveAccountsResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    data: z.array(inactiveAccountSchema),
    pagination: paginationSchema,
  }),
  message: z.string(),
});

// Terminated Account schemas
const terminatedAccountSchema = z.object({
  id: z.number(),
  accountId: z.string(),
  accountName: z.string(),
  platform: z.string(),
  balance: z.number(),
  challengeType: z.string(),
  status: z.string(),
  terminatedAt: z.string(),
  user: z.object({
    id: z.number(),
    email: z.string(),
    fullName: z.string(),
  }),
  failureDetails: z.object({
    phase: z.number().optional(),
    reason: z.string().optional(),
    failedAt: z.string().optional(),
    finalProfit: z.number().optional(),
    maxDrawdown: z.number().optional(),
  }).optional(),
});

const terminatedAccountsResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    accounts: z.array(terminatedAccountSchema),
    pagination: z.object({
      currentPage: z.number(),
      totalPages: z.number(),
      totalCount: z.number(),
      limit: z.number(),
      hasNextPage: z.boolean(),
      hasPrevPage: z.boolean(),
    }),
  }),
  message: z.string(),
});

// Discounted Accounts schemas
const discountedAccountUserSchema = z.object({
  id: z.number(),
  email: z.string(),
  fullName: z.string(),
  userName: z.string(),
  phone: z.string().nullable(),
  picture: z.string().nullable(),
  emailVerified: z.boolean(),
  createdAt: z.string(),
});

const discountedAccountSchema = z.object({
  userId: z.number(),
  accountId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  user: discountedAccountUserSchema,
});

const discountedAccountsResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    discountedAccounts: z.array(discountedAccountSchema),
    pagination: z.object({
      currentPage: z.number(),
      totalPages: z.number(),
      totalCount: z.number(),
      limit: z.number(),
      hasMore: z.boolean(),
    }),
  }),
  message: z.string(),
});

// Coupon schemas
const couponSchema = z.object({
  id: z.number(),
  code: z.string(),
  description: z.string(),
  discountType: z.enum(['percentage', 'fixed']),
  discountValue: z.number(),
  minOrderAmount: z.number(),
  maxDiscountAmount: z.number().nullable(),
  usageLimit: z.number(),
  usedCount: z.number(),
  isActive: z.boolean(),
  validFrom: z.string(),
  validUntil: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const createCouponSchema = z.object({
  code: z.string(),
  description: z.string(),
  discountType: z.enum(['percentage', 'fixed']),
  discountValue: z.number(),
  minOrderAmount: z.number(),
  maxDiscountAmount: z.number().nullable(),
  usageLimit: z.number(),
  validFrom: z.string(),
  validUntil: z.string(),
});

const updateCouponSchema = z.object({
  code: z.string().optional(),
  description: z.string().optional(),
  discountType: z.enum(['percentage', 'fixed']).optional(),
  discountValue: z.number().optional(),
  minOrderAmount: z.number().optional(),
  maxDiscountAmount: z.number().nullable().optional(),
  usageLimit: z.number().optional(),
  isActive: z.boolean().optional(),
  validFrom: z.string().optional(),
  validUntil: z.string().optional(),
});

const couponsResponseSchema = z.object({
  success: z.boolean(),
  data: z.union([
    z.array(couponSchema),
    z.object({
      data: z.array(couponSchema),
      pagination: paginationSchema,
    }),
  ]),
  message: z.string(),
});

const couponResponseSchema = z.object({
  success: z.boolean(),
  data: couponSchema,
  message: z.string(),
});

const validateCouponSchema = z.object({
  code: z.string(),
  orderAmount: z.number(),
});

const couponValidationSchema = z.object({
  id: z.number(),
  code: z.string(),
  description: z.string(),
  discountType: z.enum(['percentage', 'fixed']),
  discountValue: z.number(),
  discountAmount: z.number(),
  finalAmount: z.number(),
  isValid: z.boolean(),
  reason: z.string().optional(),
});

const couponValidationResponseSchema = z.object({
  success: z.boolean(),
  data: couponValidationSchema,
  message: z.string(),
});

// Certificate schemas
const certificateSchema = z.object({
  id: z.number(),
  type: z.enum(['payout', 'funding', 'performance', 'passverification', 'passchallenge']),
  certificate: z.string(), // This is the certificate URL
  accountId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  mtAccount: z.object({
    accountName: z.string(),
    platform: z.string(),
    challengeType: z.string(),
    user: z.object({
      id: z.number(),
      email: z.string(),
      fullName: z.string(),
      profile: z.object({
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
      }),
    }),
  }),
});

const createCertificateSchema = z.object({
  type: z.enum(['payout', 'funding', 'performance', 'passverification', 'passchallenge']),
  name: z.string().min(1).max(100),
  amount: z.number().optional(),
  date: z.string().optional(),
  // Prefer login (backend resolves the broker identity and stamps it onto
  // the Certificate row). accountId is legacy fallback. If neither is given,
  // the backend mints a random UUID for a one-off cert.
  login: z.string().optional(),
  server: z.string().optional(),
  platform: z.string().optional(),
  accountId: z.string().optional(),
  performance: z.object({
    profitAchieved: z.string().optional(),
    timeToPass: z.string().optional(),
    maxDrawdown: z.string().optional(),
    strategyUsed: z.string().optional(),
    challengeType: z.string().optional(),
    riskProfile: z.string().optional(),
  }).optional(),
});

const certificatesPaginationSchema = z.object({
  currentPage: z.number(),
  totalPages: z.number(),
  totalCertificates: z.number(),
  limit: z.number(),
  hasNextPage: z.boolean(),
  hasPrevPage: z.boolean(),
});

const certificatesResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    certificates: z.array(certificateSchema),
    pagination: certificatesPaginationSchema,
  }),
  message: z.string(),
});

const certificateResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    certificate: z.object({
      url: z.string(),
      type: z.enum(['payout', 'funding', 'performance', 'passverification', 'passchallenge']),
      accountId: z.string(),
      recipientName: z.string(),
      amount: z.number().nullable(),
      generatedAt: z.string(),
    }),
  }),
  message: z.string(),
});

const certificateVerificationSchema = z.object({
  valid: z.boolean(),
  certificate: certificateSchema.nullable(),
  message: z.string(),
});

const certificateVerificationResponseSchema = z.object({
  success: z.boolean(),
  data: certificateVerificationSchema,
  message: z.string(),
});

// Promo Card schemas
const promoCardSchema = z.object({
  id: z.number(),
  variantId: z.string(),
  tagText: z.string(),
  title: z.string(),
  description: z.string(),
  buttonText: z.string(),
  buttonLink: z.string().optional(),
  timerText: z.string(),
  initialDays: z.number(),
  initialHours: z.number(),
  initialMins: z.number(),
  seatsLeft: z.number().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const createPromoCardSchema = z.object({
  variantId: z.string(),
  tagText: z.string(),
  title: z.string(),
  description: z.string(),
  buttonText: z.string(),
  buttonLink: z.string().optional(),
  timerText: z.string(),
  initialDays: z.number(),
  initialHours: z.number(),
  initialMins: z.number(),
  seatsLeft: z.number().nullable().optional(),
  isActive: z.boolean().optional(),
});

const updatePromoCardSchema = z.object({
  tagText: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  buttonText: z.string().optional(),
  buttonLink: z.string().optional(),
  timerText: z.string().optional(),
  initialDays: z.number().optional(),
  initialHours: z.number().optional(),
  initialMins: z.number().optional(),
  seatsLeft: z.number().nullable().optional(),
  isActive: z.boolean().optional(),
});

const promoCardsResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(promoCardSchema),
});

const promoCardResponseSchema = z.object({
  success: z.boolean(),
  data: promoCardSchema,
  message: z.string().optional(),
});

const deleteResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

// Basic user type for dropdowns
const basicUserSchema = z.object({
  id: z.number(),
  email: z.string(),
  fullName: z.string(),
  userName: z.string(),
  emailVerified: z.boolean(),
  phone: z.string().nullable(),
  createdAt: z.string(),
  displayName: z.string(),
});

// VPS schemas
const vpsSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
  ip: z.string(),
  username: z.string(),
  password: z.string().nullable().optional(),
  terminalsCount: z.number().optional().default(0),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

const vpsListResponseSchema = z.object({
  success: z.boolean().optional(),
  data: z.union([
    z.array(vpsSchema),
    z.object({
      data: z.array(vpsSchema),
      pagination: paginationSchema.optional(),
    }),
    z.object({
      vps: z.array(vpsSchema),
      pagination: paginationSchema.optional(),
    }),
  ]),
  message: z.string().optional(),
});

// VPS credentials (secrets) schema — password-only.
const vpsCredentialsSchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  name: z.string().optional(),
  ip: z.string().optional(),
  username: z.string().optional(),
  password: z.string().nullable().optional(),
});

const vpsCredentialsResponseSchema = z.object({
  success: z.boolean().optional(),
  data: vpsCredentialsSchema,
  message: z.string().optional(),
});

// Terminal schemas
const terminalSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
  vpsId: z.union([z.string(), z.number()]),
  usersConnectedCount: z.number().optional().default(0),
  usersCount: z.number().optional().default(0),
  assignedAccountsCount: z.number().optional().default(0),
  assignedAccounts: z
    .array(
      z.object({
        accountId: z.string(),
        sourceTable: z.string().optional(),
        userId: z.union([z.string(), z.number()]).optional(),
        login: z.string().nullable().optional(),
        email: z.string().nullable().optional(),
        fullName: z.string().nullable().optional(),
        userName: z.string().nullable().optional(),
        label: z.string().optional(),
      })
    )
    .optional()
    .default([]),
  status: z.enum(["ACTIVE", "STOPPED", "DISCONNECTED"]),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  vps: z
    .object({
      ip: z.string().optional(),
      username: z.string().optional(),
    })
    .optional(),
});

const terminalsListResponseSchema = z.object({
  success: z.boolean().optional(),
  data: z.union([
    z.array(terminalSchema),
    z.object({
      data: z.array(terminalSchema),
      pagination: paginationSchema.optional(),
    }),
    z.object({
      terminals: z.array(terminalSchema),
      pagination: paginationSchema.optional(),
    }),
  ]),
  message: z.string().optional(),
});

const assignableTerminalAccountSchema = z.object({
  accountId: z.string(),
  login: z.string().optional(),
  userId: z.union([z.string(), z.number()]),
  email: z.string().optional(),
  fullName: z.string().optional(),
  userName: z.string().optional(),
  sourceTable: z.string(),
  updatedAt: z.string().optional(),
  label: z.string(),
});

const assignableTerminalAccountsResponseSchema = z.object({
  success: z.boolean().optional(),
  data: z.object({
    accounts: z.array(assignableTerminalAccountSchema),
    pagination: paginationSchema,
  }),
  message: z.string().optional(),
});

// Trades schemas
const tradeSchema = z.object({
  _id: z.string(),
  gain: z.number(),
  pips: z.number(),
  type: z.string(),
  profit: z.number(),
  symbol: z.string(),
  volume: z.number(),
  success: z.string(),
  openTime: z.string(),
  closeTime: z.string(),
  userId: z.number(),
  accountLogin: z.string(),
  accountInfo: z.object({
    accountId: z.string(),
    accountName: z.string(),
    platform: z.string(),
    challengeType: z.string(),
  }).optional(),
  user: z.object({
    id: z.number(),
    email: z.string(),
    fullName: z.string(),
  }),
});

const tradesSummarySchema = z.object({
  totalUsers: z.number(),
  totalAccounts: z.number(),
  totalTrades: z.number(),
  profitableTrades: z.number(),
  losingTrades: z.number(),
  totalProfit: z.number(),
  winRate: z.number(),
});

const tradesResponseSchema = z.object({
  status: z.string(),
  message: z.string(),
  data: z.object({
    summary: tradesSummarySchema,
    users: z.array(z.object({
      user: z.object({
        id: z.number(),
        email: z.string(),
        fullName: z.string(),
      }),
      accounts: z.array(z.object({
        accountId: z.string(),
        accountInfo: z.object({
          accountId: z.string(),
          accountName: z.string(),
          platform: z.string(),
          challengeType: z.string(),
        }).optional(),
        totalTrades: z.number(),
        filteredTrades: z.number(),
      })),
      totalTrades: z.number(),
    })),
    trades: z.array(tradeSchema),
    pagination: z.object({
      limit: z.number(),
      offset: z.number(),
      hasMore: z.boolean(),
      totalTrades: z.number(),
    }),
  }),
});


// Performance Data schemas
const performanceDataSchema = z.object({
  id: z.number(),
  data: z.record(z.any()), // JSONB data
  createdAt: z.string(),
  updatedAt: z.string(),
});

const performanceDataResponseSchema = z.object({
  success: z.boolean(),
  data: z.record(z.any()), // The actual performance data from JSONB
  message: z.string(),
});

const updatePerformanceDataSchema = z.object({
  data: z.record(z.any()), // Any JSON structure
});

// Leaderboard schemas
const leaderboardSchema = z.object({
  id: z.number(),
  rank: z.number(),
  name: z.string(),
  flag: z.string(),
  location: z.string(),
  accountSize: z.string(),
  winRate: z.string(),
  profitFactor: z.number(),
  totalTrades: z.number(),
  monthlyReturn: z.string(),
  status: z.enum(['increasing', 'decreasing']),
  accountCategory: z.enum(['All', '$5k', '$10k', '$25k', '$50k', '$100k', '$200k', '$500k', '$1M']),
  isActive: z.boolean(),
  isTopThree: z.boolean(),
  trophyIcon: z.enum(['trophy1', 'trophy2', 'trophy3']).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const createLeaderboardSchema = z.object({
  rank: z.number(),
  name: z.string(),
  flag: z.string(),
  location: z.string(),
  accountSize: z.string(),
  winRate: z.string(),
  profitFactor: z.number(),
  totalTrades: z.number(),
  monthlyReturn: z.string(),
  status: z.enum(['increasing', 'decreasing']),
  accountCategory: z.enum(['All', '$5k', '$10k', '$25k', '$50k', '$100k', '$200k', '$500k', '$1M']),
  trophyIcon: z.enum(['trophy1', 'trophy2', 'trophy3']).nullable().optional(),
});

const updateLeaderboardSchema = z.object({
  rank: z.number().optional(),
  name: z.string().optional(),
  flag: z.string().optional(),
  location: z.string().optional(),
  accountSize: z.string().optional(),
  winRate: z.string().optional(),
  profitFactor: z.number().optional(),
  totalTrades: z.number().optional(),
  monthlyReturn: z.string().optional(),
  status: z.enum(['increasing', 'decreasing']).optional(),
  accountCategory: z.enum(['All', '$5k', '$10k', '$25k', '$50k', '$100k', '$200k', '$500k', '$1M']).optional(),
  isActive: z.boolean().optional(),
  trophyIcon: z.enum(['trophy1', 'trophy2', 'trophy3']).nullable().optional(),
});

const leaderboardResponseSchema = z.object({
  success: z.boolean(),
  data: z.union([
    z.array(leaderboardSchema),
    z.object({
      data: z.array(leaderboardSchema),
      pagination: paginationSchema,
    }),
  ]),
  message: z.string(),
});

const leaderboardItemResponseSchema = z.object({
  success: z.boolean(),
  data: leaderboardSchema,
  message: z.string(),
});

// Banner schemas
const bannerSchema = z.object({
  id: z.number(),
  text: z.string(),
  isActive: z.boolean(),
  linkUrl: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const createBannerSchema = z.object({
  text: z.string(),
  isActive: z.boolean().optional(),
  linkUrl: z.string().nullable().optional(),
});

const updateBannerSchema = z.object({
  text: z.string().optional(),
  isActive: z.boolean().optional(),
  linkUrl: z.string().nullable().optional(),
});

const bannerResponseSchema = z.object({
  success: z.boolean(),
  data: bannerSchema.nullable(),
  message: z.string(),
});

// Account Banner schemas (landing-page marquee controlled by admin)
const accountBannerSchema = z.object({
  id: z.number(),
  text: z.string(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const updateAccountBannerSchema = z.object({
  text: z.string().optional(),
  isActive: z.boolean().optional(),
});

const accountBannerResponseSchema = z.object({
  success: z.boolean(),
  data: accountBannerSchema.nullable(),
  message: z.string(),
});

// Extended Banner schemas (landing-page extended/promo section)
const extendedBannerSchema = z.object({
  id: z.number(),
  highlightTitle: z.string(),
  titleSuffix: z.string(),
  bodyPart1: z.string(),
  bodyHighlight1: z.string(),
  bodyPart2: z.string(),
  bodyHighlight2: z.string(),
  bodyPart3: z.string(),
  ctaLabel: z.string(),
  ctaUrl: z.string(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const updateExtendedBannerSchema = z.object({
  highlightTitle: z.string().optional(),
  titleSuffix: z.string().optional(),
  bodyPart1: z.string().optional(),
  bodyHighlight1: z.string().optional(),
  bodyPart2: z.string().optional(),
  bodyHighlight2: z.string().optional(),
  bodyPart3: z.string().optional(),
  ctaLabel: z.string().optional(),
  ctaUrl: z.string().optional(),
  isActive: z.boolean().optional(),
});

const extendedBannerResponseSchema = z.object({
  success: z.boolean(),
  data: extendedBannerSchema.nullable(),
  message: z.string(),
});

// Signature schemas
const signatureSchema = z.object({
  id: z.string(),
  userId: z.number(),
  documentId: z.string(),
  status: z.enum(['preparing', 'sent', 'completed']),
  signUrl: z.string().nullable(),
  downloadUrl: z.string().nullable(),
  signerName: z.string(),
  signerEmail: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  signedAt: z.string().nullable(),
  user: z.object({
    id: z.number(),
    email: z.string(),
    fullName: z.string(),
    emailVerified: z.boolean(),
  }),
});

const signaturesResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    signatures: z.array(signatureSchema),
    pagination: paginationSchema,
  }),
  message: z.string(),
});

// Types
export type PerformanceData = z.infer<typeof performanceDataSchema>;
export type PerformanceDataResponse = z.infer<typeof performanceDataResponseSchema>;
export type UpdatePerformanceData = z.infer<typeof updatePerformanceDataSchema>;
export type AdminLeaderboard = z.infer<typeof leaderboardSchema>;
export type CreateLeaderboard = z.infer<typeof createLeaderboardSchema>;
export type UpdateLeaderboard = z.infer<typeof updateLeaderboardSchema>;
export type LeaderboardResponse = z.infer<typeof leaderboardResponseSchema>;
export type LeaderboardItemResponse = z.infer<typeof leaderboardItemResponseSchema>;
export type AdminUser = z.infer<typeof userSchema>;
export type BasicUser = z.infer<typeof basicUserSchema>;
export type AdminUserAccount = z.infer<typeof userAccountSchema>;
export type AdminUserActivity = z.infer<typeof userActivitySchema>;
export type UserActivitiesResponse = z.infer<typeof userActivitiesResponseSchema>;
export type AdminPayment = z.infer<typeof paymentSchema>;
export type DashboardSummary = z.infer<typeof dashboardSummarySchema>;
export type PaymentsResponse = z.infer<typeof paymentsResponseSchema>;
export type UserAccountsResponse = z.infer<typeof userAccountsResponseSchema>;
export type AccountsResponse = z.infer<typeof accountsResponseSchema>;
export type PoolAccountsResponse = z.infer<typeof poolAccountsResponseSchema>;

/** One pool's health, scoped to the token that currently assigns that type. */
export interface AccountPoolView {
  accountType: 'demo' | 'live';
  token: { id: number; name: string } | null;
  // One row per (platform, balance) the assigning token holds. Derived from the
  // accounts themselves, so a size shows up whether or not AccountPool has a
  // configured target for it.
  pools: Array<{
    id: string;
    platform: string;
    balance: number;
    available: number;
    assigned: number;
    targetPoolSize: number;
  }>;
  totals: { available: number; assigned: number; target: number };
}

/** A MetaApi account, as the admin UI sees it. The token string is never sent. */
export interface MetaApiToken {
  id: number;
  name: string;
  tokenPreview: string | null;
  isActive: boolean;
  isDemoAssignment: boolean;
  isLiveAssignment: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { accounts: number };
}
export type AdminPoolAccount = z.infer<typeof poolAccountSchema>;
export type PaginationInfo = z.infer<typeof paginationSchema>;
export type PaginatedData<T> = {
  data: T[];
  pagination: PaginationInfo;
};
export type PayoutRequest = z.infer<typeof payoutRequestSchema>;
export type PayoutRequestResponse = z.infer<typeof payoutRequestResponseSchema>;
export type AdminPayoutRequest = z.infer<typeof adminPayoutRequestSchema>;
export type AdminPayoutRequestsResponse = z.infer<typeof adminPayoutRequestsResponseSchema>;
export type UpdatePayoutRequest = z.infer<typeof updatePayoutRequestSchema>;
export type UpdatePayoutRequestResponse = z.infer<typeof updatePayoutRequestResponseSchema>;
export type AdminSubscriber = z.infer<typeof subscriberSchema>;
export type SubscribersResponse = z.infer<typeof subscribersResponseSchema>;
export type AdminInactiveAccount = z.infer<typeof inactiveAccountSchema>;
export type InactiveAccountsResponse = z.infer<typeof inactiveAccountsResponseSchema>;
export type AdminTerminatedAccount = z.infer<typeof terminatedAccountSchema>;
export type TerminatedAccountsResponse = z.infer<typeof terminatedAccountsResponseSchema>;
export type DiscountedAccountUser = z.infer<typeof discountedAccountUserSchema>;
export type DiscountedAccount = z.infer<typeof discountedAccountSchema>;
export type DiscountedAccountsResponse = z.infer<typeof discountedAccountsResponseSchema>;
export type AdminCoupon = z.infer<typeof couponSchema>;
export type CreateCoupon = z.infer<typeof createCouponSchema>;
export type UpdateCoupon = z.infer<typeof updateCouponSchema>;
export type CouponsResponse = z.infer<typeof couponsResponseSchema>;
export type CouponResponse = z.infer<typeof couponResponseSchema>;
export type ValidateCoupon = z.infer<typeof validateCouponSchema>;
export type CouponValidation = z.infer<typeof couponValidationSchema>;
export type CouponValidationResponse = z.infer<typeof couponValidationResponseSchema>;
export type AdminCertificate = z.infer<typeof certificateSchema>;
export type CreateCertificate = z.infer<typeof createCertificateSchema>;
export type CertificatesResponse = z.infer<typeof certificatesResponseSchema>;
export type CertificateResponse = z.infer<typeof certificateResponseSchema>;
export type CertificateVerification = z.infer<typeof certificateVerificationSchema>;
export type CertificateVerificationResponse = z.infer<typeof certificateVerificationResponseSchema>;
export type CertificatesPagination = z.infer<typeof certificatesPaginationSchema>;
export type PromoCard = z.infer<typeof promoCardSchema>;
export type CreatePromoCard = z.infer<typeof createPromoCardSchema>;
export type UpdatePromoCard = z.infer<typeof updatePromoCardSchema>;
export type PromoCardsResponse = z.infer<typeof promoCardsResponseSchema>;
export type PromoCardResponse = z.infer<typeof promoCardResponseSchema>;
export type AdminTrade = z.infer<typeof tradeSchema>;
export type TradesSummary = z.infer<typeof tradesSummarySchema>;
export type TradesResponse = z.infer<typeof tradesResponseSchema>;
export type Banner = z.infer<typeof bannerSchema>;
export type CreateBanner = z.infer<typeof createBannerSchema>;
export type UpdateBanner = z.infer<typeof updateBannerSchema>;
export type BannerResponse = z.infer<typeof bannerResponseSchema>;
export type AccountBanner = z.infer<typeof accountBannerSchema>;
export type UpdateAccountBanner = z.infer<typeof updateAccountBannerSchema>;
export type AccountBannerResponse = z.infer<typeof accountBannerResponseSchema>;
export type ExtendedBanner = z.infer<typeof extendedBannerSchema>;
export type UpdateExtendedBanner = z.infer<typeof updateExtendedBannerSchema>;
export type ExtendedBannerResponse = z.infer<typeof extendedBannerResponseSchema>;
export type AdminSignature = z.infer<typeof signatureSchema>;
export type SignaturesResponse = z.infer<typeof signaturesResponseSchema>;
export type AdminVps = z.infer<typeof vpsSchema>;
export type AdminVpsCredentials = z.infer<typeof vpsCredentialsSchema>;
export type AdminTerminal = z.infer<typeof terminalSchema>;
export type AssignableTerminalAccount = z.infer<typeof assignableTerminalAccountSchema>;

export type CreateVpsPayload = {
  name: string;
  ip: string;
  username: string;
  password: string;
};

export type CreateTerminalPayload = {
  name: string;
  vpsId: string | number;
  status: "ACTIVE" | "STOPPED" | "DISCONNECTED";
  assignments: Array<{
    accountId: string;
    sourceTable: string;
    userId: string | number;
  }>;
};

// Customer Profile types
export interface CustomerProfileKycDetails {
  full_name: string;
  country: string;
  id_type: string;
  sumsub_id: string | null;
}

export interface CustomerProfileAccountDetail {
  account_number: string;
  account_login_number: string;
  vps_server_id: string;
  terminal_number: string;
  program_type: string;
  account_status: string;
  account_size: number;
  platform: string;
}

export interface CustomerProfileEsignSummary {
  total: number;
  completed: number;
  pending: number;
  latest_status: string | null;
  latest_signed_at: string | null;
  latest_updated_at: string | null;
  contract_signatures: number;
}

export interface CustomerProfileData {
  customer_id: number;
  name: string | null;
  email: string;
  phone: string | null;
  kyc_status: string;
  kyc_details: CustomerProfileKycDetails | null;
  accounts_owned: number;
  program_types: string[];
  account_sizes: number[];
  vps_server: string[];
  terminal_number: string[];
  account_numbers: string[];
  payment_history: Array<Record<string, any>>;
  esign_summary: CustomerProfileEsignSummary;
  accounts_detail: CustomerProfileAccountDetail[];
}

export interface CustomerProfileResponse {
  success: boolean;
  message: string;
  data: CustomerProfileData;
}

// Payment Discount Settings types
export interface PaymentDiscountSettings {
  id: number;
  cryptoDiscountPercent: number;
  discountText: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdatePaymentDiscountSettings {
  cryptoDiscountPercent?: number;
  discountText?: string;
  isActive?: boolean;
}

export interface PaymentDiscountResponse {
  success: boolean;
  data: PaymentDiscountSettings;
  message: string;
}


const userAccountAssignmentSchema = z.object({
  userId: z.number(),
  platform: z.string(),
  balance: z.number(),
  challengeType: z.string(),
  amount: z.number().optional(),
  useBackupPool: z.boolean().optional(),
  // Optional admin-targeted pick. When set, the backend assigns this specific
  // login/server from the chosen pool instead of auto-picking the first match.
  targetLogin: z.string().optional(),
  targetServer: z.string().optional(),
});

export interface PoolAvailableAccount {
  id: number;
  login: string;
  server: string;
  platform: string;
  balance: number;
  accountName: string;
  accountId: string | null;
}

const poolHealthSchema = z.object({
  totalPools: z.number(),
  activePools: z.number(),
  healthyPools: z.number(),
  criticalPools: z.number(),
  overallHealth: z.number(),
  poolDetails: z.array(z.object({
    id: z.number(),
    platform: z.string(),
    balance: z.number(),
    currentSize: z.number(),
    targetSize: z.number(),
    minimumThreshold: z.number(),
    isHealthy: z.boolean(),
  })),
});

const availablePoolAccountSchema = z.object({
  id: z.number(),
  platform: z.string(),
  balance: z.number(),
  accountId: z.string(),
  accountName: z.string(),
  createdAt: z.string(),
});

// New types
export type UserAccountAssignment = z.infer<typeof userAccountAssignmentSchema>;
export type PoolHealth = z.infer<typeof poolHealthSchema>;
export type AvailablePoolAccount = z.infer<typeof availablePoolAccountSchema>;

// Role Management types
const roleSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  pages: z.array(z.string()),
  userCount: z.number().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// User with Role types
const userWithRoleSchema = z.object({
  id: z.number(),
  email: z.string(),
  fullName: z.string(),
  userName: z.string(),
  emailVerified: z.boolean(),
  phone: z.string().nullable(),
  createdAt: z.string(),
  role: z.object({
    id: z.number(),
    name: z.string(),
    description: z.string(),
    pages: z.array(z.string()),
  }).nullable(),
  totalAccounts: z.number().optional(),
  totalPayments: z.number().optional(),
});

const usersWithRolesResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    users: z.array(userWithRoleSchema),
    pagination: z.object({
      currentPage: z.number(),
      totalPages: z.number(),
      totalUsers: z.number(),
      limit: z.number(),
      hasNextPage: z.boolean(),
      hasPrevPage: z.boolean(),
    }).optional(),
  }),
  message: z.string(),
});

const rolesResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    roles: z.array(roleSchema),
    pagination: z.object({
      currentPage: z.number(),
      totalPages: z.number(),
      totalRoles: z.number(),
      limit: z.number(),
      hasNextPage: z.boolean(),
      hasPrevPage: z.boolean(),
    }).optional(),
  }),
  message: z.string(),
});

const roleResponseSchema = z.object({
  success: z.boolean(),
  data: roleSchema,
  message: z.string(),
});

// Trading Rules types
const tradingRuleSchema = z.object({
  id: z.number(),
  title: z.string(),
  content: z.array(z.string()),
  order: z.number(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const tradingRulesResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    data: z.array(tradingRuleSchema),
    pagination: z.object({
      currentPage: z.number(),
      totalPages: z.number(),
      totalItems: z.number(),
      itemsPerPage: z.number(),
      hasNextPage: z.boolean(),
      hasPrevPage: z.boolean(),
    }).optional(),
    filters: z.record(z.any()).optional(),
  }),
  message: z.string(),
});

const tradingRuleResponseSchema = z.object({
  success: z.boolean(),
  data: tradingRuleSchema,
  message: z.string(),
});

export type Role = z.infer<typeof roleSchema>;
export type RolesResponse = z.infer<typeof rolesResponseSchema>;
export type RoleResponse = z.infer<typeof roleResponseSchema>;
export type UserWithRole = z.infer<typeof userWithRoleSchema>;
export type UsersWithRolesResponse = z.infer<typeof usersWithRolesResponseSchema>;

export type TradingRule = z.infer<typeof tradingRuleSchema>;
export type TradingRulesResponse = z.infer<typeof tradingRulesResponseSchema>;
export type TradingRuleResponse = z.infer<typeof tradingRuleResponseSchema>;

// Filter interfaces
export interface AdminFilterParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

interface PaymentFilterParams extends AdminFilterParams {
  method?: string;
  platform?: string;
  dateFrom?: string;
  dateTo?: string;
}


interface PoolAccountFilterParams extends AdminFilterParams {
  platform?: string;
  balance?: number;
}

// Admin API endpoints
export const admin = {
  // Dashboard Summary
  getDashboardSummary: async (params?: AdminFilterParams) => {
    const response = await apiClient.get<{ data: DashboardSummary }>('/admin/summary', {
      params,
    });
    return response.data;
  },

  // User Management
  getUsers: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/users', {
      params,
    });
    return response.data;
  },

  // VPS Management
  // Nexcore VPS custom status — admin-set, keyed by the Nexcore account email.
  getNexcoreVpsStatuses: async () => {
    const response = await apiClient.get<{
      success: boolean;
      data: { statuses: Array<{ email: string; status: string; updatedAt: string }> };
      message: string;
    }>('/admin/nexcore-vps/statuses');
    return response.data;
  },

  setNexcoreVpsStatus: async (email: string, status: string) => {
    const response = await apiClient.patch('/admin/nexcore-vps/statuses', { email, status });
    return response.data;
  },

  getVps: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/vps', { params });
    return vpsListResponseSchema.parse(response.data);
  },

  createVps: async (payload: CreateVpsPayload) => {
    const response = await apiClient.post('/admin/vps', payload);
    return response.data;
  },

  bulkCreateVps: async (payload: { vps: CreateVpsPayload[]; skipDuplicates?: boolean }) => {
    const response = await apiClient.post('/admin/vps/bulk', payload);
    return response.data as {
      data: {
        created: number;
        skipped: number;
        skippedDetails: Array<{ row: number; ip: string; reason: string }>;
      };
      message?: string;
    };
  },

  getVpsCredentials: async (vpsId: string | number) => {
    const response = await apiClient.get(`/admin/vps/${vpsId}/credentials`);
    return vpsCredentialsResponseSchema.parse(response.data);
  },

  // Terminals Management
  getTerminals: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/terminals', { params });
    return terminalsListResponseSchema.parse(response.data);
  },

  createTerminal: async (payload: CreateTerminalPayload) => {
    const response = await apiClient.post('/admin/terminals', payload);
    return response.data;
  },

  getAssignableTerminalAccounts: async (params?: { page?: number; limit?: number; search?: string }) => {
    const response = await apiClient.get('/admin/terminals/assignable-accounts', { params });
    return assignableTerminalAccountsResponseSchema.parse(response.data);
  },

  // VPS accounts (new direct VPS → account flow)
  listVpsAccounts: async (vpsId: number) => {
    const response = await apiClient.get(`/admin/vps/${vpsId}/accounts`);
    return response.data;
  },
  addVpsAccount: async (vpsId: number, data: { login: string }) => {
    const response = await apiClient.post(`/admin/vps/${vpsId}/accounts`, data);
    return response.data;
  },
  bulkAddVpsAccounts: async (vpsId: number, data: { logins: string[]; skipDuplicates: boolean }) => {
    const response = await apiClient.post(`/admin/vps/${vpsId}/accounts/bulk`, data);
    return response.data;
  },
  bulkAssignVpsAccounts: async (data: {
    rows: Array<{ login: string; vpsName: string }>;
    skipDuplicates?: boolean;
  }) => {
    const response = await apiClient.post(`/admin/vps/accounts/bulk-assign`, data);
    return response.data as {
      data: {
        created: number;
        skipped: number;
        skippedDetails: Array<{ row: number; login: string; reason: string }>;
      };
      message?: string;
    };
  },
  removeVpsAccount: async (vpsId: number, id: number) => {
    const response = await apiClient.delete(`/admin/vps/${vpsId}/accounts/${id}`);
    return response.data;
  },
  listAllVpsAccounts: async (params?: Record<string, string>) => {
    const response = await apiClient.get(`/admin/vps/accounts`, { params });
    return response.data;
  },
  deleteVpsAccountById: async (id: number) => {
    const response = await apiClient.delete(`/admin/vps/accounts/${id}`);
    return response.data;
  },
  getUnassignedVpsAccounts: async () => {
    const response = await apiClient.get(`/admin/vps/accounts/unassigned`);
    return response.data;
  },
  assignVpsAccount: async (id: number, vpsId: number) => {
    const response = await apiClient.patch(`/admin/vps/accounts/${id}/assign`, { vpsId });
    return response.data;
  },
  searchVpsAssignableAccounts: async (q: string) => {
    const response = await apiClient.get('/admin/vps/accounts/search', { params: { q } });
    return response.data;
  },

  getCustomerProfile: async (userId: number) => {
    const response = await apiClient.get<CustomerProfileResponse>(`/admin/users/${userId}/customer-profile`);
    return response.data;
  },

  getUserAccounts: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/user-accounts', {
      params,
    });
    return response.data;
  },
  getUserActivity: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/activities', {
      params,
    });
    return response.data;
  },

  getAccounts: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/accounts', {
      params,
    });
    return response.data;
  },

  // Pool health for one pool. Availability is counted against the token that
  // currently assigns that type - the only stock a purchase can draw from.
  getPoolAccounts: async (accountType: 'demo' | 'live', params?: AdminFilterParams) => {
    const response = await apiClient.get<{ data: AccountPoolView }>('/admin/account-pool', {
      params: { ...params, accountType },
    });
    return response.data;
  },

  // Payment Management
  getPayments: async (params?: PaymentFilterParams) => {
    const response = await apiClient.get('/admin/payments', {
      params,
    });
    return response.data;
  },

  // Payout Request
  createPayoutRequest: async (payoutData: PayoutRequest) => {
    const response = await apiClient.post<PayoutRequestResponse>('/admin/payout-request', payoutData);
    return response.data;
  },

  // Payout Requests Management
  getPayoutRequests: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/payout-requests', {
      params,
    });
    return response.data;
  },

  updatePayoutRequest: async (id: number, updateData: UpdatePayoutRequest) => {
    const response = await apiClient.put(`/admin/payout-requests/${id}/status`, updateData);
    return response.data;
  },


  // Account Pool Operations
  updateAccountPool: async (id: number, updateData: any) => {
    const response = await apiClient.put(`/admin/account-pool/${id}`, updateData);
    return response.data;
  },

  createAccountPool: async (poolData: any) => {
    const response = await apiClient.post('/admin/account-pool', poolData);
    return response.data;
  },

  createPooledAccount: async (accountData: { platform: string; balance: number }) => {
    const response = await apiClient.post('/admin/create-pooled-account', accountData);
    return response.data;
  },

  refreshPoolCounts: async (accountType: 'demo' | 'live') => {
    const response = await apiClient.get('/admin/refresh-pool-counts', {
      params: { accountType },
    });
    return response.data;
  },


  // User Account Assignment
  assignAccountToUser: async (assignmentData: UserAccountAssignment) => {
    const response = await apiClient.post('/admin/assign-account-to-user', assignmentData);
    return response.data;
  },

  createAccountForUser: async (accountData: UserAccountAssignment) => {
    const response = await apiClient.post('/admin/create-account-for-user', accountData);
    return response.data;
  },

  // New unified assign account endpoint
  assignAccount: async (assignmentData: UserAccountAssignment & { isNew: boolean }) => {
    // Use longer timeout for account assignment (2 minutes)
    const response = await apiClient.post('/admin/assign-account', assignmentData, {
      timeout: 120000, // 2 minutes
    });
    return response.data;
  },

  getUserAccountHistory: async (userId: number, params?: PaginationParams) => {
    const response = await apiClient.get(`/admin/user-account-history/${userId}`, { params });
    return response.data;
  },

  getAvailablePoolAccounts: async (params?: PoolAccountFilterParams) => {
    const response = await apiClient.get('/admin/available-pool-accounts', { params });
    return response.data;
  },

  // Pool browsing for the admin assign-from-pool flow. Pool can be 'main' or
  // 'backup'. Returns the distinct list of servers that have unassigned
  // accounts matching the filters.
  getPoolServers: async (params: {
    pool: 'main' | 'backup';
    platform: string;
    balance: number;
    challengeType?: string;
  }) => {
    const response = await apiClient.get<{ data: string[] }>('/admin/pool/servers', { params });
    return response.data;
  },

  // List the unassigned pool accounts on a specific server.
  getPoolAccountsByServer: async (params: {
    pool: 'main' | 'backup';
    platform: string;
    balance: number;
    server: string;
    challengeType?: string;
  }) => {
    const response = await apiClient.get<{ data: PoolAvailableAccount[] }>('/admin/pool/accounts', { params });
    return response.data;
  },

  // Subscriber Management
  getSubscribers: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/subscribers', {
      params,
    });
    return response.data;
  },

  // Inactive Accounts Management
  getInactiveAccounts: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/inactive-accounts', {
      params,
    });
    return response.data;
  },

  // Terminated Accounts Management
  getTerminatedAccounts: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/accounts/terminated', {
      params,
    });
    return response.data;
  },

  // Discounted Accounts Management
  getDiscountedAccounts: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/discounted-accounts', {
      params,
    });
    return response.data;
  },

  // MetaApi token management. A token is the MetaApi account that owns a set of
  // trading accounts; two of them are flagged as the demo and live assignment
  // tokens, which is what the pool draws from. The token string itself never
  // leaves the server - responses carry `tokenPreview` only.
  getMetaApiTokens: async () => {
    const response = await apiClient.get<{ data: MetaApiToken[] }>('/admin/metaapi-tokens');
    return response.data;
  },

  createMetaApiToken: async (data: { name: string; token: string; isActive?: boolean }) => {
    const response = await apiClient.post('/admin/metaapi-tokens', data);
    return response.data;
  },

  updateMetaApiToken: async (
    id: number,
    data: { name?: string; token?: string; isActive?: boolean },
  ) => {
    const response = await apiClient.put(`/admin/metaapi-tokens/${id}`, data);
    return response.data;
  },

  // Makes this token the demo or live assignment token, clearing the flag from
  // every other token in the same transaction.
  setMetaApiTokenAssignment: async (id: number, accountType: 'demo' | 'live') => {
    const response = await apiClient.put(`/admin/metaapi-tokens/${id}/assignment`, { accountType });
    return response.data;
  },

  deleteMetaApiToken: async (id: number) => {
    const response = await apiClient.delete(`/admin/metaapi-tokens/${id}`);
    return response.data;
  },

  // MTAccount Management. Every account lives in one table now; the token it
  // belongs to is a column, filterable via params.tokenId.
  getMTAccounts: async (params?: AdminFilterParams & { tokenId?: number }) => {
    const response = await apiClient.get('/admin/mtaccounts/mtaccounts', {
      params,
    });
    return response.data;
  },

  createDiscountedAccount: async (data: { userId: number; login: string }) => {
    const response = await apiClient.post('/admin/discounted-accounts', data);
    return response.data;
  },

  createMTAccount: async (data: {
    accountSize: string;
    login: string;
    server: string;
    password: string;
    investorPassword?: string;
    platform?: string;
    brokerName?: string;
    accountName?: string;
    accountType: 'demo' | 'live';
    accountId?: string;
    tokenId: number;
    // Optional: assign to a user on creation instead of pooling the account.
    userId?: number;
    challengeType?: string;
  }) => {
    const response = await apiClient.post('/admin/mtaccount', data);
    return response.data;
  },

  bulkCreateMTAccounts: async (data: {
    accounts: Array<{
      accountSize: string;
      login: string;
      server: string;
      password: string;
      investorPassword?: string;
      platform?: string;
      brokerName?: string;
      accountName?: string;
      accountType: 'demo' | 'live';
      accountId?: string;
    }>;
    skipDuplicates: boolean;
    tokenId: number;
  }) => {
    const response = await apiClient.post('/admin/mtaccount/bulk', data);
    return response.data;
  },

  // Coupon Management
  getCoupons: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/coupons', {
      params,
    });
    return response.data;
  },

  createCoupon: async (couponData: CreateCoupon) => {
    const response = await apiClient.post('/admin/coupons', couponData);
    return response.data;
  },

  updateCoupon: async (id: number, couponData: UpdateCoupon) => {
    const response = await apiClient.put(`/admin/coupons/${id}`, couponData);
    return response.data;
  },

  deleteCoupon: async (id: number) => {
    const response = await apiClient.delete(`/admin/coupons/${id}`);
    return response.data;
  },

  // Certificate Management
  getCertificates: async (params?: AdminFilterParams & { type?: string }) => {
    const response = await apiClient.get('/admin/certificates', {
      params,
    });
    return response.data;
  },

  generateCertificate: async (certificateData: CreateCertificate) => {
    const response = await apiClient.post('/admin/certificates/generate', certificateData);
    return response.data;
  },

  // Promo Card Management
  getActivePromoCards: async () => {
    const response = await apiClient.get('/promo-cards/active');
    return promoCardsResponseSchema.parse(response.data);
  },

  getAllPromoCards: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/promo-cards', {
      params,
    });
    return promoCardsResponseSchema.parse(response.data);
  },

  getPromoCardById: async (id: number) => {
    const response = await apiClient.get(`/admin/promo-cards/${id}`);
    return promoCardResponseSchema.parse(response.data);
  },

  getPromoCardByVariantId: async (variantId: string) => {
    const response = await apiClient.get(`/admin/promo-cards/variant/${encodeURIComponent(variantId)}`);
    return promoCardResponseSchema.parse(response.data);
  },

  createPromoCard: async (data: CreatePromoCard) => {
    const validated = createPromoCardSchema.parse(data);
    const response = await apiClient.post('/admin/promo-cards', validated);
    return promoCardResponseSchema.parse(response.data);
  },

  updatePromoCardById: async (id: number, data: UpdatePromoCard) => {
    const validated = updatePromoCardSchema.parse(data);
    const response = await apiClient.put(`/admin/promo-cards/${id}`, validated);
    return promoCardResponseSchema.parse(response.data);
  },

  updatePromoCardByVariantId: async (variantId: string, data: UpdatePromoCard) => {
    const validated = updatePromoCardSchema.parse(data);
    const response = await apiClient.put(`/admin/promo-cards/variant/${encodeURIComponent(variantId)}`, validated);
    return promoCardResponseSchema.parse(response.data);
  },

  togglePromoCardStatus: async (id: number) => {
    const response = await apiClient.patch(`/admin/promo-cards/${id}/toggle`, {});
    return promoCardResponseSchema.parse(response.data);
  },

  deletePromoCard: async (id: number) => {
    const response = await apiClient.delete(`/admin/promo-cards/${id}`);
    return deleteResponseSchema.parse(response.data);
  },

  // Account Management Actions
  passAccount: async (data: { accountId: string; profit: number }) => {
    const response = await apiClient.post('/admin/accounts/pass', data);
    return response.data;
  },

  terminateAccount: async (data: { accountId: string; reason: string; notes?: string }) => {
    const response = await apiClient.post('/admin/accounts/terminate', data);
    return response.data;
  },

  updateAccountStatus: async (data: { login?: string; server?: string; platform?: string; accountId?: string; status: string; reason?: string }) => {
    const response = await apiClient.put('/admin/accounts/status', data);
    return response.data;
  },

  // Contract Management
  getContracts: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/contracts', {
      params,
    });
    return response.data;
  },

  // Trades Management
  getUserTrades: async (params?: {
    userId: number; // Required for single user
    limit?: number;
    offset?: number;
    accountId?: string;
    symbol?: string;
    success?: string;
    sortBy?: string;
    sortOrder?: string;
    dateFrom?: string;
    dateTo?: string;
    minProfit?: number;
    maxProfit?: number;
    search?: string;
  }) => {
    const response = await apiClient.get('/meta/get-user-trades', {
      params: params || {},
    });
    return response.data;
  },

  getAllUsersTrades: async (params?: {
    limit?: number;
    offset?: number;
    userId?: number; // Optional for all users
    accountId?: string;
    symbol?: string;
    success?: string;
    sortBy?: string;
    sortOrder?: string;
    dateFrom?: string;
    dateTo?: string;
    minProfit?: number;
    maxProfit?: number;
    search?: string;
  }) => {
    const response = await apiClient.get('/meta/get-all-users-trades', {
      params: params || {},
    });
    return response.data;
  },

  // Performance Data Management
  getPerformanceData: async () => {
    const response = await apiClient.get<PerformanceDataResponse>('/admin/performance-data');
    return response.data;
  },

  initializePerformanceData: async (performanceData: any) => {
    const response = await apiClient.post<PerformanceDataResponse>('/admin/performance-data/init', performanceData);
    return response.data;
  },

  updatePerformanceData: async (performanceData: any) => {
    const response = await apiClient.put<PerformanceDataResponse>('/admin/performance-data', performanceData);
    return response.data;
  },

  deletePerformanceData: async () => {
    const response = await apiClient.delete('/admin/performance-data');
    return response.data;
  },

  // Popup Configuration Management
  getPopupConfig: async () => {
    const response = await apiClient.get('/admin/popup-config');
    return response.data;
  },

  updatePopupConfig: async (config: any) => {
    const response = await apiClient.put('/admin/popup-config', config);
    return response.data;
  },

  uploadPopupIcon: async (formData: FormData) => {
    const response = await apiClient.post('/admin/popup-config/upload-icon', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Leaderboard Management
  getLeaderboard: async (params?: AdminFilterParams & { category?: string }) => {
    const response = await apiClient.get('/admin/leaderboard', {
      params,
    });
    return response.data;
  },

  createLeaderboard: async (leaderboardData: CreateLeaderboard) => {
    const response = await apiClient.post('/admin/leaderboard', leaderboardData);
    return response.data;
  },

  updateLeaderboard: async (id: number, leaderboardData: UpdateLeaderboard) => {
    const response = await apiClient.put(`/admin/leaderboard/${id}`, leaderboardData);
    return response.data;
  },

  deleteLeaderboard: async (id: number) => {
    const response = await apiClient.delete(`/admin/leaderboard/${id}`);
    return response.data;
  },

  // Banner Configuration Management
  getBannerConfig: async () => {
    const response = await apiClient.get<BannerResponse>('/admin/banner-config');
    return response.data;
  },

  updateBannerConfig: async (bannerData: UpdateBanner) => {
    const response = await apiClient.put<BannerResponse>('/admin/banner-config', bannerData);
    return response.data;
  },

  // Account Banner (landing-page marquee) Management
  getAccountBannerConfig: async () => {
    const response = await apiClient.get<AccountBannerResponse>('/admin/account-banner-config');
    return response.data;
  },

  updateAccountBannerConfig: async (data: UpdateAccountBanner) => {
    const response = await apiClient.put<AccountBannerResponse>('/admin/account-banner-config', data);
    return response.data;
  },

  // Extended Banner (landing-page "extended through" section) Management
  getExtendedBannerConfig: async () => {
    const response = await apiClient.get<ExtendedBannerResponse>('/admin/extended-banner-config');
    return response.data;
  },

  updateExtendedBannerConfig: async (data: UpdateExtendedBanner) => {
    const response = await apiClient.put<ExtendedBannerResponse>('/admin/extended-banner-config', data);
    return response.data;
  },

  // Trading Rules Management
  getTradingRules: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/trading-rules', {
      params,
    });
    return response.data;
  },

  getTradingRuleById: async (id: number) => {
    const response = await apiClient.get(`/admin/trading-rules/${id}`);
    return response.data;
  },

  createTradingRule: async (ruleData: {
    title: string;
    content: string[];
    isActive?: boolean;
    order?: number;
  }) => {
    const response = await apiClient.post('/admin/trading-rules', ruleData);
    return response.data;
  },

  updateTradingRule: async (id: number, ruleData: {
    title?: string;
    content?: string[];
    isActive?: boolean;
    order?: number;
  }) => {
    const response = await apiClient.put(`/admin/trading-rules/${id}`, ruleData);
    return response.data;
  },

  deleteTradingRule: async (id: number) => {
    const response = await apiClient.delete(`/admin/trading-rules`, { data: { id } });
    return response.data;
  },

  updateTradingRuleOrder: async (id: number, order: number) => {
    const response = await apiClient.put(`/admin/trading-rules/${id}/order`, { id, order });
    return response.data;
  },

  bulkUpdateTradingRuleOrders: async (orders: Array<{ id: number; order: number }>) => {
    const response = await apiClient.put('/admin/trading-rules/bulk-update-orders', { orders });
    return response.data;
  },

  toggleTradingRuleActive: async (id: number) => {
    const response = await apiClient.patch(`/admin/trading-rules/${id}/toggle-active`, { id });
    return response.data;
  },

  // Role Management
  getRoles: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/roles', {
      params,
    });
    return response.data;
  },

  getRoleById: async (id: number) => {
    const response = await apiClient.get(`/admin/roles/${id}`);
    return response.data;
  },

  createRole: async (roleData: {
    name: string;
    description: string;
    pages: string[];
  }) => {
    const response = await apiClient.post('/admin/roles', roleData);
    return response.data;
  },

  updateRole: async (id: number, roleData: {
    name?: string;
    description?: string;
    pages?: string[];
  }) => {
    const response = await apiClient.patch(`/admin/roles/${id}`, roleData);
    return response.data;
  },

  deleteRole: async (id: number) => {
    const response = await apiClient.delete(`/admin/roles/${id}`);
    return response.data;
  },

  // User Role Assignment
  getUsersWithRoles: async (params?: AdminFilterParams) => {
    const response = await apiClient.get('/admin/users-with-roles', {
      params,
    });
    return response.data;
  },

  updateUserRole: async (userId: number, roleId: number) => {
    const response = await apiClient.patch(`/admin/users/${userId}/role`, {
      roleId,
    });
    return response.data;
  },

  toggleManualKyc: async (userId: number) => {
    const response = await apiClient.patch(`/admin/users/${userId}/manual-kyc`);
    return response.data;
  },

  // Payment Discount Settings Management
  getPaymentDiscountSettings: async () => {
    const response = await apiClient.get('/admin/payment-discount-settings');
    return response.data;
  },

  updatePaymentDiscountSettings: async (discountData: {
    cryptoDiscountPercent?: number;
    discountText?: string;
    isActive?: boolean;
  }) => {
    const response = await apiClient.put('/admin/payment-discount-settings', discountData);
    return response.data;
  },

  // TradeMate buy-one-get-one offer management
  getTradeMateOfferSettings: async () => {
    const response = await apiClient.get('/admin/trademate-offer-settings');
    return response.data;
  },

  updateTradeMateOfferSettings: async (data: { isActive: boolean }) => {
    const response = await apiClient.put('/admin/trademate-offer-settings', data);
    return response.data;
  },

  getActiveDiscountInfo: async () => {
    const response = await apiClient.get('/admin/payment-discount-settings/active');
    return response.data;
  },

  // Paytiko Settings Management
  getPaytikoSettings: async () => {
    const response = await apiClient.get('/admin/paytiko-settings');
    return response.data;
  },

  updatePaytikoSettings: async (data: { isEnabled: boolean }) => {
    const response = await apiClient.put('/admin/paytiko-settings', data);
    return response.data;
  },

  getActivePaytikoSettings: async () => {
    const response = await apiClient.get('/admin/paytiko-settings/active');
    return response.data;
  },

  // Signatures Management
  getSignatures: async (params?: AdminFilterParams & { 
    status?: 'preparing' | 'sent' | 'completed';
    dateFrom?: string;
    dateTo?: string;
  }) => {
    const response = await apiClient.get('/admin/signatures', {
      params,
    });
    return response.data;
  },

  // Trademate Management
  getTradeMateAccounts: async (params?: AdminFilterParams & {
    status?: string;
    challengeType?: string;
    userId?: string;
  }) => {
    const response = await apiClient.get<AdminTradeMateAccountsResponse>('/admin/trade-mate/accounts', {
      params,
    });
    return response.data;
  },

  assignTradeMateAccount: async (data: {
    userId: number;
    challengeType: string;
    balance: number;
    // Price charged for the account. Recorded on the admin Payment record so
    // the assignment counts as a paid sale (same as the MT4/MT5 assign flow).
    amount?: number;
  }) => {
    const response = await apiClient.post('/admin/trade-mate/accounts/assign', data);
    return response.data;
  },

  updateTradeMateAccountStatus: async (data: {
    accountId: string;
    status: 'ACTIVE' | 'SUSPENDED' | 'CLOSED';
    reason?: string;
  }) => {
    const response = await apiClient.put('/admin/trade-mate/accounts/status', data);
    return response.data;
  },

  terminateTradeMateAccount: async (data: {
    accountId: string;
    reason: string;
    notes?: string;
  }) => {
    const response = await apiClient.post('/admin/trade-mate/accounts/terminate', data);
    return response.data;
  },

};

// Helper functions for admin operations
export const adminHelpers = {
  formatUserStatus: (status: boolean) => status ? 'Active' : 'Inactive',
  
  formatPaymentStatus: (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      paid: { label: 'Paid', color: 'text-emerald-400' },
      pending: { label: 'Pending', color: 'text-yellow-400' },
      failed: { label: 'Failed', color: 'text-red-400' },
      cancelled: { label: 'Cancelled', color: 'text-gray-400' },
      approved: { label: 'Approved', color: 'text-green-400' },
      rejected: { label: 'Rejected', color: 'text-red-400' },
    };
    return statusMap[status.toLowerCase()] || { label: status, color: 'text-gray-300' };
  },

  formatAccountStatus: (status: string | null | undefined) => {
    // for accounts admin.ts:1122 Uncaught TypeError: Cannot read properties of null (reading 'toLowerCase') resolved
    if (!status) {
      return { label: 'N/A', color: 'text-gray-400' };
    }
    const statusMap: Record<string, { label: string; color: string }> = {
      pooled: { label: 'Pooled', color: 'text-blue-400' },
      active: { label: 'Active', color: 'text-emerald-400' },
      failed: { label: 'Failed', color: 'text-red-400' },
      remote_sync_failed: { label: 'Failed', color: 'text-red-400' },
      passed: { label: 'Passed', color: 'text-green-400' },
      suspended: { label: 'Suspended', color: 'text-orange-400' },
      pending: { label: 'Pending', color: 'text-yellow-400' },
    };
    return statusMap[status.toLowerCase()] || { label: status, color: 'text-gray-300' };
  },

  formatPoolStatus: (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      assigned: { label: 'Assigned', color: 'text-emerald-400' },
      unassigned: { label: 'Unassigned', color: 'text-yellow-400' },
    };
    return statusMap[status.toLowerCase()] || { label: status, color: 'text-gray-300' };
  },

  formatPlatform: (platform?: string | null) => (platform ? platform.toUpperCase() : 'N/A'),

  formatChallengeType: (type: string) => {
    if (!type) return 'N/A';
    switch (type) {
      case 'twoPhase': return 'Two Phase';
      case 'onePhase': return 'One Phase';
      case 'instantFund': return 'Instant Funding Pro'; // backward compat
      case 'instantFundingPro': return 'Instant Funding Pro';
      case 'instantFundingLite': return 'Instant Funding Lite';
      default: return type;
    }
  },

  formatDate: (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  },

  formatCurrency: (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  },


  formatPoolProgress: (current: number, target: number) => {
    const percentage = Math.min((current / target) * 100, 100);
    return `${current}/${target} (${percentage.toFixed(0)}%)`;
  },

  formatCouponStatus: (isActive: boolean) => {
    return isActive ? 
      { label: 'Active', color: 'text-emerald-400' } : 
      { label: 'Inactive', color: 'text-red-400' };
  },

  formatDiscountType: (type: string) => {
    return type === 'percentage' ? 'Percentage' : 'Fixed Amount';
  },

  formatDiscount: (type: string, value: number) => {
    return type === 'percentage' ? `${value}%` : adminHelpers.formatCurrency(value);
  },

  formatCouponUsage: (used: number, limit: number) => {
    const percentage = Math.min((used / limit) * 100, 100);
    return `${used}/${limit} (${percentage.toFixed(0)}%)`;
  },

  formatCouponDates: (from: string, until: string) => {
    const fromDate = new Date(from).toLocaleDateString();
    const untilDate = new Date(until).toLocaleDateString();
    return `${fromDate} - ${untilDate}`;
  },

  isCouponExpired: (validUntil: string) => {
    return new Date(validUntil) < new Date();
  },

  isCouponActive: (validFrom: string, validUntil: string) => {
    const now = new Date();
    const from = new Date(validFrom);
    const until = new Date(validUntil);
    return now >= from && now <= until;
  },

  formatCertificateType: (type: string) => {
    const typeMap: Record<string, string> = {
      payout: 'Payout',
      funding: 'Funding',
      performance: 'Performance',
      passverification: 'Pass Verification',
      passchallenge: 'Pass Challenge',
    };
    return typeMap[type] || type;
  },

  formatCertificateStatus: (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      active: { label: 'Active', color: 'text-emerald-400' },
      inactive: { label: 'Inactive', color: 'text-gray-400' },
      expired: { label: 'Expired', color: 'text-red-400' },
    };
    return statusMap[status.toLowerCase()] || { label: status, color: 'text-gray-300' };
  },

  formatSignatureStatus: (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      preparing: { label: 'Preparing', color: 'text-yellow-400' },
      sent: { label: 'Sent', color: 'text-blue-400' },
      completed: { label: 'Completed', color: 'text-green-400' },
    };
    return statusMap[status.toLowerCase()] || { label: status, color: 'text-gray-300' };
  },
};
